// The semantic role map: role name → category, base gain, and render recipe.
//
// Character rules (dcs-product-sound): success = ascending major third,
// error = descending/darker, warning = double pulse, notification = bell-like
// FM, tap = filtered noise burst, whoosh = filtered noise sweep. All tonal
// content stays in one key (C major family) so the pack coheres, and inside the
// 200Hz–2kHz fundamental band for accessibility (age-related HF loss).
//
// Base gains are pre-master; the engine multiplies by config.volume and clamps
// at 0.8. With the portal's subtle-tier master (0.3), notification roles land
// at ~0.045–0.084 effective — the subtle band the carve-out requires, while the
// loudness hierarchy (error > warning > success > info > interaction) holds.

import type { RoleSpec, SoundRole } from './types'
import { playFmBell, playNoiseBurst, playTone } from './synth'

// C-major family fundamentals (Hz).
const C5 = 523.25
const D5 = 587.33
const E5 = 659.25
const G5 = 783.99
const C6 = 1046.5
const G4 = 392.0
const A4 = 440.0
const D4 = 293.66

export const ROLES: Record<SoundRole, RoleSpec> = {
  // ── interaction ────────────────────────────────────────────────────────
  'interaction.tap': {
    category: 'interaction',
    gain: 0.25,
    duration: 0.06,
    render: (ctx, dest, now, gain) =>
      playNoiseBurst(ctx, dest, { dur: 0.05, gain, cutoff: 1800, q: 1 }, now),
  },
  'interaction.subtle': {
    category: 'interaction',
    gain: 0.12,
    duration: 0.04,
    render: (ctx, dest, now, gain) =>
      playNoiseBurst(ctx, dest, { dur: 0.035, gain, cutoff: 2400, q: 0.7 }, now),
  },
  'interaction.toggle': {
    category: 'interaction',
    gain: 0.2,
    duration: 0.07,
    render: (ctx, dest, now, gain) =>
      playTone(ctx, dest, { freq: E5, type: 'triangle', dur: 0.07, gain, attack: 0.005 }, now),
  },
  'interaction.confirm': {
    category: 'interaction',
    gain: 0.3,
    duration: 0.09,
    render: (ctx, dest, now, gain) =>
      playTone(ctx, dest, { freq: C5, type: 'sine', dur: 0.09, gain, attack: 0.008 }, now),
  },

  // ── overlay ────────────────────────────────────────────────────────────
  'overlay.open': {
    category: 'overlay',
    gain: 0.18,
    duration: 0.18,
    render: (ctx, dest, now, gain) =>
      playNoiseBurst(ctx, dest, { dur: 0.18, gain, cutoff: 700, q: 0.8, sweepTo: 2200 }, now),
  },
  'overlay.close': {
    category: 'overlay',
    gain: 0.18,
    duration: 0.18,
    render: (ctx, dest, now, gain) =>
      playNoiseBurst(ctx, dest, { dur: 0.18, gain, cutoff: 2200, q: 0.8, sweepTo: 600 }, now),
  },
  'overlay.expand': {
    category: 'overlay',
    gain: 0.16,
    duration: 0.16,
    render: (ctx, dest, now, gain) =>
      playTone(ctx, dest, { freq: C5, type: 'sine', dur: 0.16, gain, glideTo: E5 }, now),
  },
  'overlay.collapse': {
    category: 'overlay',
    gain: 0.16,
    duration: 0.16,
    render: (ctx, dest, now, gain) =>
      playTone(ctx, dest, { freq: E5, type: 'sine', dur: 0.16, gain, glideTo: C5 }, now),
  },

  // ── navigation ─────────────────────────────────────────────────────────
  'navigation.forward': {
    category: 'navigation',
    gain: 0.2,
    duration: 0.14,
    render: (ctx, dest, now, gain) =>
      playTone(ctx, dest, { freq: D5, type: 'sine', dur: 0.13, gain, glideTo: G5 }, now),
  },
  'navigation.backward': {
    category: 'navigation',
    gain: 0.2,
    duration: 0.14,
    render: (ctx, dest, now, gain) =>
      playTone(ctx, dest, { freq: G5, type: 'sine', dur: 0.13, gain, glideTo: D5 }, now),
  },
  'navigation.tab': {
    category: 'navigation',
    gain: 0.18,
    duration: 0.1,
    render: (ctx, dest, now, gain) =>
      playTone(ctx, dest, { freq: A4, type: 'triangle', dur: 0.09, gain, attack: 0.005 }, now),
  },

  // ── notification ─────────────────────────────────────────────────────────
  'notification.info': {
    category: 'notification',
    gain: 0.15,
    duration: 0.4,
    render: (ctx, dest, now, gain) =>
      playFmBell(ctx, dest, { freq: E5, ratio: 2, index: 90, dur: 0.42, gain }, now),
  },
  'notification.success': {
    // Ascending major third (C5 → E5) — unmistakably "up" vs the error contour.
    category: 'notification',
    gain: 0.2,
    duration: 0.34,
    render: (ctx, dest, now, gain) => {
      playTone(ctx, dest, { freq: C5, type: 'sine', dur: 0.14, gain, attack: 0.008 }, now)
      playTone(ctx, dest, { freq: E5, type: 'sine', dur: 0.2, gain, attack: 0.008 }, now + 0.11)
    },
  },
  'notification.warning': {
    // Double pulse on one note — attention without alarm.
    category: 'notification',
    gain: 0.24,
    duration: 0.3,
    render: (ctx, dest, now, gain) => {
      playTone(ctx, dest, { freq: A4, type: 'triangle', dur: 0.1, gain, attack: 0.006 }, now)
      playTone(ctx, dest, { freq: A4, type: 'triangle', dur: 0.1, gain, attack: 0.006 }, now + 0.16)
    },
  },
  'notification.error': {
    // Descending, darker (G4 → D4, triangle) — never a harsh klaxon.
    category: 'notification',
    gain: 0.28,
    duration: 0.42,
    render: (ctx, dest, now, gain) => {
      playTone(ctx, dest, { freq: G4, type: 'triangle', dur: 0.16, gain, attack: 0.008 }, now)
      playTone(ctx, dest, { freq: D4, type: 'triangle', dur: 0.26, gain, attack: 0.008 }, now + 0.13)
    },
  },

  // ── hero (disabled by default even when sound is on) ──────────────────────
  'hero.complete': {
    category: 'hero',
    gain: 0.4,
    duration: 0.9,
    render: (ctx, dest, now, gain) => {
      playTone(ctx, dest, { freq: C5, type: 'sine', dur: 0.2, gain, attack: 0.01 }, now)
      playTone(ctx, dest, { freq: E5, type: 'sine', dur: 0.2, gain, attack: 0.01 }, now + 0.16)
      playTone(ctx, dest, { freq: G5, type: 'sine', dur: 0.4, gain, attack: 0.01 }, now + 0.32)
    },
  },
  'hero.milestone': {
    category: 'hero',
    gain: 0.5,
    duration: 1.3,
    render: (ctx, dest, now, gain) => {
      playTone(ctx, dest, { freq: C5, type: 'sine', dur: 0.22, gain, attack: 0.01 }, now)
      playTone(ctx, dest, { freq: E5, type: 'sine', dur: 0.22, gain, attack: 0.01 }, now + 0.18)
      playTone(ctx, dest, { freq: G5, type: 'sine', dur: 0.22, gain, attack: 0.01 }, now + 0.36)
      playTone(ctx, dest, { freq: C6, type: 'sine', dur: 0.55, gain, attack: 0.01 }, now + 0.54)
    },
  },
}
